import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { createSlice } from '@reduxjs/toolkit';
import { NewOrderType, OrderStatus, OrderTemplateType, OrderType } from '../../types/order/order.type';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import {
  cancelOrderDetails,
  confirmRMAOrderDelivery,
  deleteInvenotryOrderDetails,
  deleteOrderDetails,
  deleteOrderFile,
  deleteOrderTemplateDetails,
  deleteRMAOrderDetails,
  failQACheck,
  getNewOrderDetails,
  getNewOrdersList,
  getOrderDetails,
  getOrdersList,
  getOrderTemplateDetails,
  getOrderTemplatesList,
  getRMAOrderDetails,
  getRMAOrdersList,
  patchNewOrderDetails,
  patchOrderDetails,
  patchOrderUpdateInvoiceStatus,
  patchOrderUpdatePaymentStatus,
  postAcceptOrder,
  postConfirmDeviceAssignment,
  resumeOrderFromHold,
  postNewOrderDetails,
  postOrderComment,
  postOrderDetails,
  postOrdersSimActivationEmail,
  postOrdersTrackingInfoEmail,
  postOrderTemplateDetails,
  postRMAOrderDetails,
  postRMAShippingPackageDetails,
  updateInventoryOrderCCNotifyUsers,
  updateOrderCCNotifyUsers,
  updateOrderStatus,
  updateOrderWarehouse,
} from './orders.action';
import { RMAOrderListItemType, RMAOrderType } from '../../views/Main/RMAOrders/utils/rmaOrders.types';

export interface OrdersState {
  orderTemplatesList: null | PaginationType<OrderTemplateType>;
  orderTemplatesListFetchStatus: FETCH_STATUS;
  orderTemplatesRouteParams: RouteParamsType;
  orderTemplateDetails: null | OrderTemplateType;
  orderTemplateDetailsFetchStatus: FETCH_STATUS;
  orderTemplateDetailsPostStatus: FETCH_STATUS;
  orderTemplateDetailsPatchStatus: FETCH_STATUS;
  orderTemplateDetailsDeleteStatus: FETCH_STATUS;

  newOrdersList: null | PaginationType<NewOrderType>;
  newOrdersListFetchStatus: FETCH_STATUS;
  newOrdersRouteParams: RouteParamsType;
  newOrderDetails: null | NewOrderType;
  newOrderDetailsFetchStatus: FETCH_STATUS;
  newOrderDetailsPostStatus: FETCH_STATUS;
  orderCommentPostStatus: FETCH_STATUS;
  newOrderDetailsPatchStatus: FETCH_STATUS;
  newOrderDetailsDeleteStatus: FETCH_STATUS;

  rmaOrderDetailsPostStatus: FETCH_STATUS;
  rmaShippingPackageDetailsPostStatus: FETCH_STATUS;
  confirmDeliveryRMAOrderPatchStatus: FETCH_STATUS;
  rmaOrderDetailsDeleteStatus: FETCH_STATUS;
  rmaOrdersList: null | PaginationType<RMAOrderListItemType>;
  rmaOrdersListFetchStatus: FETCH_STATUS;
  rmaOrdersRouteParams: RouteParamsType;
  rmaOrderDetails: null | RMAOrderType;
  rmaOrderDetailsFetchStatus: FETCH_STATUS;

  ordersList: null | PaginationType<OrderType>;
  ordersListFetchStatus: FETCH_STATUS;
  ordersRouteParams: RouteParamsType;
  orderDetails: null | OrderType;
  orderDetailsFetchStatus: FETCH_STATUS;
  orderDetailsPostStatus: FETCH_STATUS;
  orderDetailsPatchStatus: FETCH_STATUS;
  orderDetailsDeleteStatus: FETCH_STATUS;
  orderDetailsCancelStatus: FETCH_STATUS;
  orderDetailsDeleteInventoryStatus: FETCH_STATUS;
  orderSimActivationDetailsPostStatus: FETCH_STATUS;
  orderTrackingInfoDetailsPostStatus: FETCH_STATUS;
  orderFileDetailsDeleteStatus: FETCH_STATUS;
  acceptOrderPostStatus: FETCH_STATUS;
  updateInventoryOrderCCNotifyUsersStatus: FETCH_STATUS;
}

export const initialOrdersRouteParams = {
  page: 1,
  limit: 10,
  order: '-orders.id',
  q: '',
  status: '',
};

export const initialOrderTemplatesRouteParams = {
  page: 1,
  limit: 10,
  order: '-obj.id',
  q: '',
};

export const initialRMAOrdersRouteParams = {
  page: 1,
  limit: 10,
  order: '-obj.id',
  q: '',
};

export const initialNewOrdersRouteParams = {
  page: 1,
  limit: 10,
  order: '-obj.createdAt',
  q: '',
  status: '',
  carrier_name: '',
  reseller_id: '',
  customer_id: '',
  warehouse_id: '',
  is_telcobill_order: '',
};

const INIT_STATE: OrdersState = {
  orderTemplatesList: null,
  orderTemplatesListFetchStatus: FETCH_STATUS.NULL,
  orderTemplatesRouteParams: initialOrderTemplatesRouteParams,
  orderTemplateDetails: null,
  orderTemplateDetailsFetchStatus: FETCH_STATUS.NULL,
  orderTemplateDetailsPostStatus: FETCH_STATUS.NULL,
  orderTemplateDetailsPatchStatus: FETCH_STATUS.NULL,
  orderTemplateDetailsDeleteStatus: FETCH_STATUS.NULL,

  newOrdersList: null,
  newOrdersListFetchStatus: FETCH_STATUS.NULL,
  newOrdersRouteParams: initialNewOrdersRouteParams,
  newOrderDetails: null,
  newOrderDetailsFetchStatus: FETCH_STATUS.NULL,
  newOrderDetailsPostStatus: FETCH_STATUS.NULL,
  orderCommentPostStatus: FETCH_STATUS.NULL,
  newOrderDetailsPatchStatus: FETCH_STATUS.NULL,
  newOrderDetailsDeleteStatus: FETCH_STATUS.NULL,

  rmaOrderDetailsPostStatus: FETCH_STATUS.NULL,
  rmaShippingPackageDetailsPostStatus: FETCH_STATUS.NULL,
  confirmDeliveryRMAOrderPatchStatus: FETCH_STATUS.NULL,
  rmaOrderDetailsDeleteStatus: FETCH_STATUS.NULL,
  rmaOrdersListFetchStatus: FETCH_STATUS.NULL,
  rmaOrdersList: null,
  rmaOrdersRouteParams: initialRMAOrdersRouteParams,
  rmaOrderDetails: null,
  rmaOrderDetailsFetchStatus: FETCH_STATUS.NULL,

  ordersRouteParams: initialOrdersRouteParams,
  ordersList: null,
  ordersListFetchStatus: FETCH_STATUS.NULL,
  orderDetails: null,
  orderDetailsFetchStatus: FETCH_STATUS.NULL,
  orderDetailsPostStatus: FETCH_STATUS.NULL,
  orderDetailsPatchStatus: FETCH_STATUS.NULL,
  orderDetailsDeleteStatus: FETCH_STATUS.NULL,
  orderDetailsCancelStatus: FETCH_STATUS.NULL,
  orderDetailsDeleteInventoryStatus: FETCH_STATUS.NULL,
  orderFileDetailsDeleteStatus: FETCH_STATUS.NULL,
  orderTrackingInfoDetailsPostStatus: FETCH_STATUS.NULL,
  orderSimActivationDetailsPostStatus: FETCH_STATUS.NULL,
  acceptOrderPostStatus: FETCH_STATUS.NULL,
  updateInventoryOrderCCNotifyUsersStatus: FETCH_STATUS.NULL,
};

export const ordersSlice = createSlice({
  name: 'orders',
  initialState: INIT_STATE,
  reducers: {
    setOrderTemplatesList: (state, action) => {
      state.orderTemplatesList = action.payload;
    },
    setOrderTemplatesListFetchStatus: (state, action) => {
      state.orderTemplatesListFetchStatus = action.payload;
    },
    setOrderTemplateDetails: (state, action) => {
      state.orderTemplateDetails = action.payload;
    },
    setOrderTemplateDetailsFetchStatus: (state, action) => {
      state.orderTemplateDetailsFetchStatus = action.payload;
    },
    setOrderTemplateDetailsPostStatus: (state, action) => {
      state.orderTemplateDetailsPostStatus = action.payload;
    },
    setOrderTemplateDetailsPatchStatus: (state, action) => {
      state.orderTemplateDetailsPatchStatus = action.payload;
    },
    setOrderTemplateDetailsDeleteStatus: (state, action) => {
      state.orderTemplateDetailsDeleteStatus = action.payload;
    },

    setNewOrdersList: (state, action) => {
      state.newOrdersList = action.payload;
    },
    setNewOrdersListFetchStatus: (state, action) => {
      state.newOrdersListFetchStatus = action.payload;
    },
    setNewOrderDetails: (state, action) => {
      state.newOrderDetails = action.payload;
    },
    setNewOrderDetailsFetchStatus: (state, action) => {
      state.newOrderDetailsFetchStatus = action.payload;
    },
    setNewOrderDetailsPostStatus: (state, action) => {
      state.newOrderDetailsPostStatus = action.payload;
    },
    setOrderCommentPostStatus: (state, action) => {
      state.orderCommentPostStatus = action.payload;
    },
    setNewOrderDetailsPatchStatus: (state, action) => {
      state.newOrderDetailsPatchStatus = action.payload;
    },
    setNewOrderDetailsDeleteStatus: (state, action) => {
      state.newOrderDetailsDeleteStatus = action.payload;
    },

    setOrdersList: (state, action) => {
      state.ordersList = action.payload;
    },
    setOrdersListFetchStatus: (state, action) => {
      state.ordersListFetchStatus = action.payload;
    },
    setOrderDetails: (state, action) => {
      state.orderDetails = action.payload;
    },
    setOrderDetailsFetchStatus: (state, action) => {
      state.orderDetailsFetchStatus = action.payload;
    },
    setOrderDetailsPostStatus: (state, action) => {
      state.orderDetailsPostStatus = action.payload;
    },
    setOrderDetailsPatchStatus: (state, action) => {
      state.orderDetailsPatchStatus = action.payload;
    },
    setOrderDetailsDeleteStatus: (state, action) => {
      state.orderDetailsDeleteStatus = action.payload;
    },
    setOrderDetailsCancelStatus: (state, action) => {
      state.orderDetailsCancelStatus = action.payload;
    },
    setOrderDetailsDeleteInventoryStatus: (state, action) => {
      state.orderDetailsDeleteInventoryStatus = action.payload;
    },
    setOrderFileDetailsDeleteStatus: (state, action) => {
      state.orderFileDetailsDeleteStatus = action.payload;
    },
    setOrderTrackingInfoDetailsPostStatus: (state, action) => {
      state.orderTrackingInfoDetailsPostStatus = action.payload;
    },
    setOrderSimActivationDetailsPostStatus: (state, action) => {
      state.orderSimActivationDetailsPostStatus = action.payload;
    },
    setAcceptOrderPostStatus: (state, action) => {
      state.acceptOrderPostStatus = action.payload;
    },
    setUpdateInventoryOrderCCNotifyUsersStatus: (state, action) => {
      state.updateInventoryOrderCCNotifyUsersStatus = action.payload;
    },
    setRMAOrderDetailsPostStatus: (state, action) => {
      state.rmaOrderDetailsPostStatus = action.payload;
    },
    setRMAShippingPackageDetailsPostStatus: (state, action) => {
      state.rmaShippingPackageDetailsPostStatus = action.payload;
    },
    setConfirmDeliveryRMAOrderPatchStatus: (state, action) => {
      state.confirmDeliveryRMAOrderPatchStatus = action.payload;
    },
    setRMAOrderDetailsDeleteStatus: (state, action) => {
      state.rmaOrderDetailsDeleteStatus = action.payload;
    },
    setRMAOrdersListFetchStatus: (state, action) => {
      state.rmaOrdersListFetchStatus = action.payload;
    },
    setRMAOrdersList: (state, action) => {
      state.rmaOrdersList = action.payload;
    },
    setRMAOrderDetailsFetchStatus: (state, action) => {
      state.rmaOrderDetailsFetchStatus = action.payload;
    },
    setRMAOrderDetails: (state, action) => {
      state.rmaOrderDetails = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'orders') {
        state.ordersRouteParams = { ...state.ordersRouteParams, ...payload.data };
      }

      if (payload.reducer === 'new-orders') {
        state.newOrdersRouteParams = { ...state.newOrdersRouteParams, ...payload.data };
      }

      if (payload.reducer === 'order-templates') {
        state.orderTemplatesRouteParams = { ...state.orderTemplatesRouteParams, ...payload.data };
      }

      if (payload.reducer === 'rma-orders') {
        state.rmaOrdersRouteParams = { ...state.rmaOrdersRouteParams, ...payload.data };
      }
    });

    // CREATE ORDERS DETAILS
    builder.addCase(postOrderDetails.pending, state => {
      state.orderDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postOrderDetails.rejected, (state, action) => {
      state.orderDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        const errorMessage = action.payload as string;
        if (errorMessage && !errorMessage.toLowerCase().includes('can not buy did')) {
          snackbarUtils.error(errorMessage);
        }
      });
    });
    builder.addCase(postOrderDetails.fulfilled, state => {
      state.orderDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Order created successfully!');
      });
    });

    // GET ORDERS LIST
    builder.addCase(getOrdersList.pending, state => {
      state.ordersListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getOrdersList.rejected, (state, action) => {
      if (action.payload) {
        state.ordersListFetchStatus = FETCH_STATUS.REJECTED;
        // state.ordersList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getOrdersList.fulfilled, (state, action) => {
      state.ordersListFetchStatus = FETCH_STATUS.FULFILLED;
      state.ordersList = action.payload;
    });

    // GET NEW ORDERS LIST
    builder.addCase(getNewOrdersList.pending, state => {
      state.newOrdersListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getNewOrdersList.rejected, (state, action) => {
      if (action.payload) {
        state.newOrdersListFetchStatus = FETCH_STATUS.REJECTED;
        // state.newOrdersList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getNewOrdersList.fulfilled, (state, action) => {
      state.newOrdersListFetchStatus = FETCH_STATUS.FULFILLED;
      state.newOrdersList = action.payload;
    });

    // GET ORDERS DETAILS
    builder.addCase(getOrderDetails.pending, state => {
      state.orderDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getOrderDetails.rejected, (state, action) => {
      state.orderDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.orderDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getOrderDetails.fulfilled, (state, action) => {
      state.orderDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.orderDetails = action.payload;
    });

    // GET ORDERS DETAILS
    builder.addCase(getNewOrderDetails.pending, state => {
      state.newOrderDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getNewOrderDetails.rejected, (state, action) => {
      state.newOrderDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.newOrderDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getNewOrderDetails.fulfilled, (state, action) => {
      state.newOrderDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.newOrderDetails = action.payload;
    });

    // GET ORDERS DETAILS
    builder.addCase(getOrderTemplateDetails.pending, state => {
      state.orderTemplateDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getOrderTemplateDetails.rejected, (state, action) => {
      state.orderTemplateDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.orderTemplateDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getOrderTemplateDetails.fulfilled, (state, action) => {
      state.orderTemplateDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.orderTemplateDetails = action.payload;
    });

    // GET ORDER TEMPLATES DETAILS
    builder.addCase(getOrderTemplatesList.pending, state => {
      state.orderTemplatesListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getOrderTemplatesList.rejected, (state, action) => {
      state.orderTemplatesListFetchStatus = FETCH_STATUS.REJECTED;
      // state.orderTemplatesList = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getOrderTemplatesList.fulfilled, (state, action) => {
      state.orderTemplatesListFetchStatus = FETCH_STATUS.FULFILLED;
      state.orderTemplatesList = action.payload;
    });

    // PATCH ORDERS DETAILS
    builder.addCase(patchOrderDetails.pending, state => {
      state.orderDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchOrderDetails.rejected, (state, action) => {
      state.orderDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchOrderDetails.fulfilled, state => {
      state.orderDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Order updated successfully!');
      });
    });

    // DELETE ORDERS DETAILS
    builder.addCase(deleteOrderDetails.pending, state => {
      state.orderDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteOrderDetails.rejected, (state, action) => {
      state.orderDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteOrderDetails.fulfilled, state => {
      state.orderDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Order deleted successfully!');
      });
    });

    // CANCEL ORDER DETAILS
    builder.addCase(cancelOrderDetails.pending, state => {
      state.orderDetailsCancelStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(cancelOrderDetails.rejected, (state, action) => {
      state.orderDetailsCancelStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(cancelOrderDetails.fulfilled, state => {
      state.orderDetailsCancelStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Order canceled successfully!');
      });
    });

    // DELETE INVENTORY ORDER DETAILS
    builder.addCase(deleteInvenotryOrderDetails.pending, state => {
      state.orderDetailsDeleteInventoryStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteInvenotryOrderDetails.rejected, (state, action) => {
      state.orderDetailsDeleteInventoryStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteInvenotryOrderDetails.fulfilled, state => {
      state.orderDetailsDeleteInventoryStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Order deleted successfully!');
      });
    });

    // DELETE ORDER TEMPLATE DETAILS
    builder.addCase(deleteOrderTemplateDetails.pending, state => {
      state.orderTemplateDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteOrderTemplateDetails.rejected, (state, action) => {
      state.orderTemplateDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteOrderTemplateDetails.fulfilled, state => {
      state.orderTemplateDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Template deleted successfully!');
      });
    });

    // DELETE ORDER FILE DETAILS
    builder.addCase(deleteOrderFile.pending, state => {
      state.orderFileDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteOrderFile.rejected, (state, action) => {
      state.orderFileDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteOrderFile.fulfilled, state => {
      state.orderFileDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Order file deleted successfully!');
      });
    });

    // POST SIM ORDERS DETAILS
    builder.addCase(postOrdersSimActivationEmail.pending, state => {
      state.orderSimActivationDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postOrdersSimActivationEmail.rejected, (state, action) => {
      state.orderSimActivationDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postOrdersSimActivationEmail.fulfilled, state => {
      state.orderSimActivationDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('SIM activation email has been sent!');
      });
    });

    // POST TRACKING ORDERS DETAILS
    builder.addCase(postOrdersTrackingInfoEmail.pending, state => {
      state.orderTrackingInfoDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postOrdersTrackingInfoEmail.rejected, (state, action) => {
      state.orderTrackingInfoDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postOrdersTrackingInfoEmail.fulfilled, state => {
      state.orderTrackingInfoDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Tracking info email has been sent!');
      });
    });

    builder.addCase(updateOrderCCNotifyUsers.rejected, (_state, action) => {
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(updateOrderCCNotifyUsers.fulfilled, (state, action) => {
      if (state.orderDetails && action.payload) {
        state.orderDetails = action.payload;
      }
    });

    builder.addCase(updateInventoryOrderCCNotifyUsers.pending, state => {
      state.updateInventoryOrderCCNotifyUsersStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateInventoryOrderCCNotifyUsers.rejected, state => {
      state.updateInventoryOrderCCNotifyUsersStatus = FETCH_STATUS.REJECTED;
    });
    builder.addCase(updateInventoryOrderCCNotifyUsers.fulfilled, state => {
      state.updateInventoryOrderCCNotifyUsersStatus = FETCH_STATUS.FULFILLED;
    });

    // POST TRACKING ORDERS DETAILS
    builder.addCase(postOrderTemplateDetails.pending, state => {
      state.orderTemplateDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postOrderTemplateDetails.rejected, (state, action) => {
      state.orderTemplateDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postOrderTemplateDetails.fulfilled, state => {
      state.orderTemplateDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Template added successfully!');
      });
    });

    // POST ORDER DETAILS
    builder.addCase(postNewOrderDetails.pending, state => {
      state.newOrderDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postNewOrderDetails.rejected, (state, action) => {
      state.newOrderDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        const errorMessage = action.payload as string;
        if (errorMessage && !errorMessage.toLowerCase().includes('can not buy did')) {
          snackbarUtils.error(errorMessage);
        }
      });
    });
    builder.addCase(postNewOrderDetails.fulfilled, state => {
      state.newOrderDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Order created successfully!');
      });
    });

    // POST ORDER DETAILS
    builder.addCase(patchNewOrderDetails.pending, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchNewOrderDetails.rejected, (state, action) => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchNewOrderDetails.fulfilled, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.FULFILLED;
    });

    // POST ORDER DETAILS
    builder.addCase(postConfirmDeviceAssignment.pending, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postConfirmDeviceAssignment.rejected, (state, action) => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postConfirmDeviceAssignment.fulfilled, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.FULFILLED;
    });

    builder.addCase(resumeOrderFromHold.pending, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(resumeOrderFromHold.rejected, (state, action) => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(resumeOrderFromHold.fulfilled, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.FULFILLED;
    });

    // PATCH ORDER DETAILS
    builder.addCase(updateOrderWarehouse.pending, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateOrderWarehouse.rejected, (state, action) => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(updateOrderWarehouse.fulfilled, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.FULFILLED;
    });

    // POST COMMENT ORDER DETAILS
    builder.addCase(postOrderComment.pending, state => {
      state.orderCommentPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postOrderComment.rejected, (state, action) => {
      state.orderCommentPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postOrderComment.fulfilled, (state, action) => {
      state.orderCommentPostStatus = FETCH_STATUS.FULFILLED;
      if (state.newOrderDetails) {
        const tmp = { ...state.newOrderDetails };
        state.newOrderDetails = {
          ...tmp,
          comments: [
            {
              ...action.payload,
              createdAt: new Date(+Date.now() - 1000).toISOString(),
            },
            ...(tmp?.comments || []),
          ],
        };
      }

      if (state.rmaOrderDetails) {
        const tmp = { ...state.rmaOrderDetails };
        state.rmaOrderDetails = {
          ...tmp,
          comments: [
            ...(tmp?.comments || []),
            {
              ...action.payload,
              createdAt: new Date(+Date.now() - 1000).toISOString(),
            },
          ],
        };
      }

      setTimeout(() => {
        snackbarUtils.success('Comment created successfully!');
      });
    });

    builder
      .addCase(updateOrderStatus.pending, state => {
        state.newOrderDetailsPatchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        if (action.meta.arg.status === OrderStatus.PendingPayment) {
          snackbarUtils.success('Invoice created and sent to customer successfully');
        }

        if (action.meta.arg.status === OrderStatus.ApprovedToFulfill) {
          snackbarUtils.success('Payment received and order approved to fulfill');
        }

        if (action.meta.arg.status === OrderStatus.StagingInProgress) {
          snackbarUtils.success('Fulfillment warehouse assigned successfully');
        }

        state.newOrderDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.newOrderDetailsPatchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      });

    // UPDATE INVOICE STATUS
    builder.addCase(patchOrderUpdateInvoiceStatus.pending, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchOrderUpdateInvoiceStatus.rejected, (state, action) => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchOrderUpdateInvoiceStatus.fulfilled, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Invoice created and sent to customer successfully');
      });
    });

    // UPDATE PAYMENT STATUS
    builder.addCase(patchOrderUpdatePaymentStatus.pending, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchOrderUpdatePaymentStatus.rejected, (state, action) => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchOrderUpdatePaymentStatus.fulfilled, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Payment received and order approved to fulfill');
      });
    });

    // FAIL QA CHECK
    builder.addCase(failQACheck.pending, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(failQACheck.rejected, (state, action) => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(failQACheck.fulfilled, state => {
      state.newOrderDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('QA check failed and reason added to internal notes');
      });
    });

    // ACCEPT ORDER
    builder.addCase(postAcceptOrder.pending, state => {
      state.acceptOrderPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAcceptOrder.rejected, (state, action) => {
      state.acceptOrderPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postAcceptOrder.fulfilled, state => {
      state.acceptOrderPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Order accepted successfully');
      });
    });

    // CREATE RMA
    builder.addCase(postRMAOrderDetails.pending, state => {
      state.rmaOrderDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postRMAOrderDetails.rejected, (state, action) => {
      state.rmaOrderDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postRMAOrderDetails.fulfilled, state => {
      state.rmaOrderDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('RMA Order created successfully!');
      });
    });

    // CREATE RMA
    builder.addCase(postRMAShippingPackageDetails.pending, state => {
      state.rmaShippingPackageDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postRMAShippingPackageDetails.rejected, (state, action) => {
      state.rmaShippingPackageDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postRMAShippingPackageDetails.fulfilled, state => {
      state.rmaShippingPackageDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('RMA shipping package created successfully!');
      });
    });

    builder.addCase(confirmRMAOrderDelivery.pending, state => {
      state.rmaShippingPackageDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(confirmRMAOrderDelivery.rejected, (state, action) => {
      state.rmaShippingPackageDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(confirmRMAOrderDelivery.fulfilled, state => {
      state.rmaShippingPackageDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Delivery confirmed!');
      });
    });

    builder.addCase(deleteRMAOrderDetails.pending, state => {
      state.rmaOrderDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteRMAOrderDetails.rejected, (state, action) => {
      state.rmaOrderDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteRMAOrderDetails.fulfilled, state => {
      state.rmaOrderDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('RMA order deleted!');
      });
    });

    builder.addCase(getRMAOrdersList.pending, state => {
      state.rmaOrdersListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getRMAOrdersList.rejected, (state, action) => {
      if (action.payload) {
        state.rmaOrdersListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getRMAOrdersList.fulfilled, (state, action) => {
      state.rmaOrdersListFetchStatus = FETCH_STATUS.FULFILLED;
      state.rmaOrdersList = action.payload;
    });

    // GET RMA DETAILS
    builder.addCase(getRMAOrderDetails.pending, state => {
      state.rmaOrderDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getRMAOrderDetails.rejected, (state, action) => {
      if (action.payload) {
        state.rmaOrderDetailsFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getRMAOrderDetails.fulfilled, (state, action) => {
      state.rmaOrderDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.rmaOrderDetails = action.payload;
    });
  },
});

export const {
  setOrderDetailsDeleteStatus,
  setOrdersListFetchStatus,
  setOrderDetailsFetchStatus,
  setOrderDetailsPatchStatus,
  setOrderDetailsPostStatus,
  setOrdersList,
  setOrderDetails,
  setOrderSimActivationDetailsPostStatus,
  setOrderTrackingInfoDetailsPostStatus,
  setOrderFileDetailsDeleteStatus,
  setOrderTemplatesListFetchStatus,
  setOrderTemplatesList,
  setOrderTemplateDetailsDeleteStatus,
  setOrderTemplateDetailsFetchStatus,
  setOrderTemplateDetailsPatchStatus,
  setOrderTemplateDetailsPostStatus,
  setOrderTemplateDetails,
  setNewOrdersList,
  setNewOrdersListFetchStatus,
  setNewOrderDetailsDeleteStatus,
  setNewOrderDetailsFetchStatus,
  setNewOrderDetailsPatchStatus,
  setNewOrderDetailsPostStatus,
  setNewOrderDetails,
  setOrderCommentPostStatus,
  setUpdateInventoryOrderCCNotifyUsersStatus,
  setRMAOrderDetailsPostStatus,
  setRMAOrdersList,
  setRMAOrdersListFetchStatus,
  setRMAOrderDetailsFetchStatus,
  setRMAOrderDetails,
  setRMAShippingPackageDetailsPostStatus,
  setConfirmDeliveryRMAOrderPatchStatus,
  setRMAOrderDetailsDeleteStatus,
} = ordersSlice.actions;

export default ordersSlice.reducer;
